import { useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { Button, Group, Skeleton, Stack } from '@mantine/core'
import { PageContainer } from '@shared/components/app-shell/page-container/page-container'
import { PAGE_TITLES } from '@/constants/page-titles'
import { useAuthenticatedGraphQuery } from '@/hooks/use-authenticated-query'
import { ViewWoolSpecification } from 'shared/src/components/speci/view-wool-specification/view-wool-specification'
import { GetSpeciStationDataGql } from '@/graphql/queries/speci/get-speci-station-data.graphql'
import { GetSpeciGql } from '@/graphql/queries/speci/get-speci.graphql'
import { useNavigation } from '@/hooks/use-navigation'
import { IconPrinter } from '@tabler/icons-react'
import { PrintOnly } from '@shared/components/print-only/print-only'
import { showErrorNotification } from '@shared/helpers/notifications/notifications'
import { useReactToPrint } from 'react-to-print'
import { SpeciWoolstoreForm } from '@/components/speci/speci-woolstore-form/speci-woolstore-form'
import { SpeciLineDtoInput } from '@loom-api-types'
import { SpeciType } from '@shared/components/speci/speci-types'
import { formatDate } from '@shared/helpers/format-date/format-date'

export function ViewSpeciPage() {
  const { navigateToStationSpecis } = useNavigation()
  const { stationRecordNumber, speciId } = useParams()

  const goToStationSpeci = () => navigateToStationSpecis(stationRecordNumber)

  useEffect(() => {
    // If no speciId is present in the URL, navigate to the station speci page
    if (!speciId && stationRecordNumber) {
      goToStationSpeci()
    }
  }, [speciId, stationRecordNumber])

  // Fetch additional station data
  const { data: stationData, isLoading: stationDataLoading } = useAuthenticatedGraphQuery({
    queryKey: ['speciStationDetails', stationRecordNumber],
    gql: GetSpeciStationDataGql,
    queryParams: { recordNumber: stationRecordNumber },
  })

  // Fetch the wool specification data
  const { data: speciData } = useAuthenticatedGraphQuery({
    queryKey: ['getSpeci', speciId],
    gql: GetSpeciGql,
    queryParams: { speciId },
  })

  // Prepare data for rendering
  const speci = speciData?.speci?.speci
  const totalBales = speci?.lines.reduce(
    (sum: number, line: SpeciLineDtoInput) => sum + line.baleNumbers.length,
    0
  )
  const sentByText =
    speci?.type === SpeciType.Grower
      ? ''
      : [speci?.sentToWoolStoreBy, formatDate(speci?.sentToWoolStoreAt, 'DD/MM/YYYY h:mm A')]
          .filter(Boolean)
          .join(' - ')

  // Print table functionality
  const printComponentRef = useRef<HTMLDivElement>(null)
  const handlePrintButtonClick = useReactToPrint({
    contentRef: printComponentRef,
  })
  const printSpeci = () => {
    try {
      handlePrintButtonClick()
    } catch (error) {
      showErrorNotification('Printing failed. Please try again.')
    }
  }

  return (
    <PageContainer title={PAGE_TITLES.WOOL_SPECIFICATIONS}>
      <Stack data-testid='view-speci-page'>
        {stationRecordNumber && speci && stationData?.stationDetailsForSpeci && totalBales ? (
          <>
            {speci?.type === SpeciType.Hub && (
              <>
                <Group justify='flex-end' mt='md' gap='xs'>
                  <Button onClick={printSpeci} leftSection={<IconPrinter />} disabled={!speci}>
                    Print speci
                  </Button>
                </Group>
                <div data-testid='print-wool-specification'>
                  <PrintOnly ref={printComponentRef}>
                    <SpeciWoolstoreForm
                      stationRecordNumber={stationRecordNumber}
                      speci={speci}
                      stationDetailsForSpeci={stationData?.stationDetailsForSpeci}
                      totalBales={totalBales}
                      sentByText={sentByText}
                    />
                  </PrintOnly>
                </div>
              </>
            )}
            <div data-testid='view-wool-specification'>
              <ViewWoolSpecification
                stationDetailsForSpeci={stationData?.stationDetailsForSpeci}
                stationDataLoading={stationDataLoading}
                speci={speci}
                stationRecordNumber={stationRecordNumber}
                totalBales={totalBales}
                sentByText={sentByText}
              />
              <Group justify='flex-end' mt='md' gap='xs'>
                <Button variant='outline' onClick={goToStationSpeci}>
                  Back
                </Button>
              </Group>
            </div>
          </>
        ) : (
          <Skeleton visible height={100} />
        )}
      </Stack>
    </PageContainer>
  )
}
